import { createSelector } from '@reduxjs/toolkit';

import type { FilesProxySchemaWithId } from '@iconik-io/iconik-api/src/services/files';

import { preferNonMultichannelProxy } from '../../../utils/prefer_non_multichannel_proxy';
import { createEntitiesIndexSelector } from '../../entities_index/selectors';
import type { BaseAppState } from '../../types';
import { getAssetOrSearchItem } from './assets';
import { getLatestActiveVersionId } from './versions';

export const selectProxiesByAssetId = createEntitiesIndexSelector(
  'proxies',
  'asset_id',
);

const getVersionId = (
  _state: BaseAppState,
  _assetId: string,
  versionId: string | undefined,
) => versionId;

export const selectClosedProxiesByAssetId = createSelector(
  selectProxiesByAssetId,
  (proxies) => proxies.filter((proxy) => proxy.status === 'CLOSED'),
);

function pushValue<K, V>(map: Map<K, V[]>, key: K, value: V) {
  const existing = map.get(key);
  if (existing) {
    existing.push(value);
  } else {
    map.set(key, [value]);
  }
}

export const selectClosedProxiesVersionMapByAssetId = createSelector(
  getAssetOrSearchItem,
  selectClosedProxiesByAssetId,
  (asset, proxies) => {
    // Since one proxy can belong to many assets and entities index support that,
    // in selectProxiesByAssetId we might get a proxy which has version_id value
    // not related to the asset we are trying to select versions map for.
    // So we explicitly get version for an asset as well, and make proxy available
    // not only by `proxy.version_id` but also by the latest asset version ID.
    // It's quite complicated and connected to subclips implementation.
    const additionalVersionId =
      asset?.type === 'SUBCLIP'
        ? getLatestActiveVersionId(asset?.versions)
        : undefined;

    const versionMap = new Map<string, FilesProxySchemaWithId[]>();
    for (const proxy of proxies) {
      if (proxy.version_id) {
        pushValue(versionMap, proxy.version_id, proxy);
      }
      if (additionalVersionId) {
        pushValue(versionMap, additionalVersionId, proxy);
      }
    }
    return versionMap;
  },
);

export function getDefaultProxy(proxies: FilesProxySchemaWithId[] | undefined) {
  if (!proxies || proxies.length === 0) {
    return undefined;
  }
  return preferNonMultichannelProxy(proxies);
}

export const selectClosedProxyByVersionId = createSelector(
  selectClosedProxiesVersionMapByAssetId,
  getVersionId,
  (proxyMap, versionId) =>
    getDefaultProxy(versionId ? proxyMap.get(versionId) : undefined),
);

export const selectAwaitedProxiesByAssetId = createSelector(
  selectProxiesByAssetId,
  (proxies) => proxies.filter((proxy) => proxy.status === 'AWAITED'),
);

export const selectAwaitedProxiesVersionMapByAssetId = createSelector(
  selectAwaitedProxiesByAssetId,
  (proxies) => {
    const versionMap = new Map<string, FilesProxySchemaWithId>();
    for (const proxy of proxies) {
      const versionId = proxy.version_id;
      if (!versionId) {
        continue;
      }
      versionMap.set(versionId, proxy);
    }
    return versionMap;
  },
);

export const selectAwaitedProxyByVersionId = createSelector(
  selectAwaitedProxiesVersionMapByAssetId,
  getVersionId,
  (proxyMap, versionId) => (versionId ? proxyMap.get(versionId) : undefined),
);
